import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IMeetingType } from '../../Interfaces/IMeetingType';
import { customFetcher } from '../useFetch';

const QUERY_KEY = 'meetingtype';

/** Nur die schreibbaren Felder an die generische API senden (Alias-Keys). */
const mapMeetingTypePayload = (meetingType: Partial<IMeetingType>) => ({
  Name: meetingType.Name,
  DurationInMin: meetingType.DurationInMin,
  BufferBeforeMin: meetingType.BufferBeforeMin ?? 0,
  BufferAfterMin: meetingType.BufferAfterMin ?? 0,
  SlotStepInMin: meetingType.SlotStepInMin ?? null,
  MinLeadTimeMinutes: meetingType.MinLeadTimeMinutes ?? null,
  BookingHorizonDays: meetingType.BookingHorizonDays ?? null,
  idLocation: meetingType.idLocation ?? null,
  idRoom: meetingType.idRoom ?? null,
  idUserCalendarConnection: meetingType.idUserCalendarConnection ?? null,
  idUser: meetingType.idUser ?? null,
  // Swagger/connexion erwartet echte Booleans (nicht 1/0).
  IsBookablePublic: Boolean(meetingType.IsBookablePublic),
  IsActive: Boolean(meetingType.IsActive),
  AllowParallel: Boolean(meetingType.AllowParallel),
});

export const useGetMeetingTypes = () => {
  const {
    data: meetingTypes,
    isLoading: isLoadingMeetingTypes,
    refetch: refetchMeetingTypes,
  } = useQuery<IMeetingType[]>({
    queryKey: [QUERY_KEY],
    queryFn: () => customFetcher({ endpoint: '/meetingtype' }),
    refetchOnWindowFocus: false,
  });

  return {
    meetingTypes: meetingTypes ?? [],
    isLoadingMeetingTypes,
    refetchMeetingTypes,
  };
};

export const useCreateMeetingType = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: createMeetingType,
    isPending: isPendingCreateMeetingType,
  } = useMutation({
    mutationFn: (meetingType: Omit<IMeetingType, 'idMeetingType'>) =>
      customFetcher({
        endpoint: '/meetingtype',
        method: 'POST',
        body: mapMeetingTypePayload(meetingType),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  return { createMeetingType, isPendingCreateMeetingType };
};

export const useUpdateMeetingType = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: updateMeetingType,
    isPending: isPendingUpdateMeetingType,
  } = useMutation({
    mutationFn: (meetingType: IMeetingType) =>
      customFetcher({
        endpoint: '/meetingtype',
        method: 'PUT',
        body: {
          idMeetingType: meetingType.idMeetingType,
          ...mapMeetingTypePayload(meetingType),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  return { updateMeetingType, isPendingUpdateMeetingType };
};

export const useDeleteMeetingType = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: deleteMeetingType,
    isPending: isPendingDeleteMeetingType,
  } = useMutation({
    mutationFn: (idMeetingType: number) =>
      customFetcher({
        endpoint: `/meetingtype/${idMeetingType}`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  return { deleteMeetingType, isPendingDeleteMeetingType };
};
